<script setup>
// FanData: banner no topo da conversa mostrando OUTRAS conversas/tickets do MESMO contato.
// Reaproveita o store nativo contactConversations (mesma fonte da seção "Conversas
// anteriores"), então é sempre atual. Espelha o do Desk Retornar, com formato TKT- do
// FanData (T-14) e filtro de "tema" adaptado às etiquetas do FanData.
import { computed, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import { formatTicket } from 'dashboard/helper/fandataTicket';

const props = defineProps({
  contactId: { type: [String, Number], required: true },
  conversationId: { type: [String, Number], required: true },
});

const store = useStore();
const route = useRoute();

// T-94 (reunião 13/08): a cor saiu do âmbar. O CpfMatchBanner logo acima e o resumo da IA logo
// abaixo já são âmbar, então este aviso se perdia no meio. Bronze escuro (sugestão do Douglas
// na call) porque é o único aviso do header que o agente precisa ler ANTES de responder.
// Tom fixo nos dois temas, não token do tema, justamente para não voltar a se misturar.

// Sentimento e protocolo não são "tema" — filtramos p/ sobrar o assunto/etiquetas úteis.
const SENTIMENTOS = ['positivo', 'neutro', 'negativo'];
const isNoise = label =>
  SENTIMENTOS.includes(label) ||
  label.startsWith('protocolo-') ||
  label === 'tolerância-zero';
// T-57 (ata 06/08): "os dois últimos tickets do contato, independentemente do status".
const MAX_SHOWN = 2;

const contactConversationGetter = useMapGetter(
  'contactConversations/getContactConversation'
);

// Ordena do mais recente para o mais antigo: o store devolve na ordem da API, e o
// pedido é pelos DOIS ÚLTIMOS — sem ordenar, o corte em MAX_SHOWN pegaria quaisquer dois.
const recencia = conversation =>
  conversation.last_activity_at || conversation.timestamp || conversation.created_at || 0;

// A conversa ATUAL nunca entra: repetir no header o ticket que já está aberto é ruído.
// Number nos dois lados porque o id chega ora string, ora número, dependendo da origem.
// A conversa ATUAL nunca entra: repetir no header o ticket que já está aberto é ruído.
// Number nos dois lados porque o id chega ora string, ora número, dependendo da origem.
const others = computed(() => {
  const atual = Number(props.conversationId);
  return contactConversationGetter
    .value(props.contactId)
    .filter(c => Number(c.id) !== atual)
    .slice()
    .sort((a, b) => recencia(b) - recencia(a));
});

const visiveis = computed(() => others.value.slice(0, MAX_SHOWN));
const restantes = computed(() => Math.max(others.value.length - MAX_SHOWN, 0));

// O "+N" leva à ficha do contato, onde está o histórico completo de tickets. Nova guia pelo
// mesmo motivo dos links: quem clica quer consultar, não abandonar o atendimento em curso.
const urlContato = computed(() =>
  frontendURL(`accounts/${route.params.accountId}/contacts/${props.contactId}`)
);

const fetchConversations = () => {
  if (props.contactId) {
    store.dispatch('contactConversations/get', props.contactId);
  }
};

onMounted(fetchConversations);
watch(() => props.contactId, fetchConversations);

const tema = conversation => {
  const labels = (conversation.labels || []).filter(label => !isNoise(label));
  return labels.slice(0, 2).join(', ');
};

const buildUrl = id =>
  frontendURL(
    conversationUrl({
      accountId: route.params.accountId,
      activeInbox: route.params.inbox_id,
      id,
    })
  );
</script>

<template>
  <div
    v-if="others.length"
    class="flex flex-wrap items-center gap-x-2 gap-y-1 px-4 py-2 text-sm border-b bg-[#8a6552] text-white border-[#6f5142] dark:bg-[#3d2c24] dark:text-[#f0dfd5] dark:border-[#2c1f1a]"
  >
    <span class="font-medium">
      ⚠️ contato com {{ others.length }} ticket(s):
    </span>
    <!-- Nova guia de propósito: o agente quer COMPARAR com a conversa atual, não sair dela. -->
    <a
      v-for="conversation in visiveis"
      :key="conversation.id"
      :href="buildUrl(conversation.id)"
      target="_blank"
      rel="noopener"
      class="font-medium underline hover:opacity-80"
    >
      {{ formatTicket(conversation.id)
      }}<template v-if="tema(conversation)"> · {{ tema(conversation) }}</template>
    </a>
    <a
      v-if="restantes"
      :href="urlContato"
      target="_blank"
      rel="noopener"
      class="font-medium underline opacity-80 hover:opacity-100"
      title="Ver todos os tickets deste contato"
    >
      +{{ restantes }}
    </a>
  </div>
</template>
